import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  MenuItem,
  FormControlLabel,
  Checkbox,
  Autocomplete,
  Divider,
  Typography,
  colors,
  Box,
  InputAdornment,
} from '@mui/material';
import { grey } from '@mui/material/colors';
import { Controller, useForm } from 'react-hook-form';
import { useMutation, useQueryClient, useQuery } from '@tanstack/react-query';
import {
  addLabourBillingRate,
  updateLabourRate,
  LabourBillingRateFormType,
  LabourBillingRate,
  Role,
  getLabourRates,
} from 'api/labourApi';
import { listClients } from 'api/clientsApi';
import { getBranches } from 'api/branchApi';
import { DatePicker, MuiPickersUtilsProvider } from '@material-ui/pickers';
import DateFnsUtils from '@date-io/date-fns';
import BranchAutocomplete from '../Utils/BranchAutocomplete';
import DuplicateConfirmationDialog from './DuplicateConfirmationDialog';
import { checkLabourBillingRateDuplication } from '../Utils/Utils';
import { useSnackbar } from 'notistack';

interface AddOrEditLabourBillingRateDialogProps {
  open: boolean;
  onClose: () => void;
  editItem: LabourBillingRate | null;
  onClearEdit?: () => void;
}

export default function AddOrEditLabourBillingRateDialog({
  open,
  onClose,
  editItem,
  onClearEdit,
}: AddOrEditLabourBillingRateDialogProps) {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();
  const clients = useQuery(['clients'], listClients);
  const { data: branches } = useQuery(['branches'], getBranches);
  const isEdit = !!editItem;
  const [duplicateDialogConfirmOpen, setDuplicateDialogConfirmOpen] =
    useState(false);
  const [duplicatingRateId, setDuplicatingRateId] = useState<string | null>(
    null
  );

  const { data: labourData } = useQuery(['labour-rates'], getLabourRates);

  const {
    control,
    handleSubmit,
    formState: { errors },
    reset,
    watch,
    setValue,
  } = useForm<LabourBillingRateFormType>();

  const effectiveFrom = watch('effectiveFrom');
  const isDefault = watch('isDefault');
  const clientId = watch('clientId');
  const branch = watch('branch');

  const addMutation = useMutation(addLabourBillingRate, {
    onSuccess: () => {
      queryClient.invalidateQueries(['labour-rates']);
      onClose();
      reset();
      onClearEdit?.();
      enqueueSnackbar('Labour billing rate added successfully', {
        variant: 'success',
      });
    },
    onError: () => {
      enqueueSnackbar('Failed to add labour billing rate', {
        variant: 'error',
      });
    },
  });

  const updateMutation = useMutation(updateLabourRate, {
    onSuccess: () => {
      queryClient.invalidateQueries(['labour-rates']);
      onClose();
      reset();
      onClearEdit?.();
      enqueueSnackbar('Labour billing rate updated successfully', {
        variant: 'success',
      });
    },
    onError: () => {
      enqueueSnackbar('Failed to update labour billing rate', {
        variant: 'error',
      });
    },
  });

  useEffect(() => {
    if (open && editItem) {
      reset({
        role: editItem.role,
        billingRate: Number(editItem.billingRate),
        workingAwayFromHomeBillingRate: Number(
          editItem.workingAwayFromHomeBillingRate ?? 0
        ),
        clientId: editItem.clientId,
        branchId: editItem.branchId,
        branch: branches?.find((b) => b.id === editItem.branchId) ?? null,
        isDefault: editItem.isDefault ?? false,
        effectiveFrom: editItem.effectiveFrom.slice(0, 10),
      });
    } else if (open) {
      reset({
        effectiveFrom: new Date().toISOString(),
        workingAwayFromHomeBillingRate: 0,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only run when dialog open or edit data changes
  }, [open, editItem]);

  const onSubmit = (data: LabourBillingRateFormType) => {
    const submitData = {
      role: data.role,
      billingRate: data.billingRate,
      workingAwayFromHomeBillingRate: data.workingAwayFromHomeBillingRate ?? 0,
      effectiveFrom: data.effectiveFrom,
      clientId: data.clientId,
      branchId: data.branch?.id,
      isDefault: data.isDefault,
    };
    const { checkRateDuplicate, foundRateId } =
      checkLabourBillingRateDuplication(
        submitData,
        labourData?.billingRates || []
      );
    if (checkRateDuplicate) {
      setDuplicateDialogConfirmOpen(true);
      setDuplicatingRateId(foundRateId);
      return;
    }
    if (editItem) {
      updateMutation.mutate({
        id: editItem.id,
        data: submitData,
        rateType: 'billingRate',
      });
    } else {
      addMutation.mutate({ data: submitData });
    }
  };

  const isSubmitting = addMutation.isLoading || updateMutation.isLoading;

  return (
    <Dialog
      open={open}
      onClose={(event, reason) => {
        if (reason !== 'backdropClick') {
          onClose();
        }
      }}
      maxWidth="md"
      fullWidth>
      <DuplicateConfirmationDialog
        alertMessage={`You already have a billing rate for the selected role on the effective date. Do you want to override the existing rate or cancel to modify the effective date?`}
        isUpdatingRate={updateMutation.isLoading}
        onConfirm={() => {
          setDuplicateDialogConfirmOpen(false);
          if (duplicatingRateId) {
            updateMutation.mutate({
              id: duplicatingRateId,
              data: {
                role: watch('role'),
                billingRate: watch('billingRate'),
                workingAwayFromHomeBillingRate:
                  watch('workingAwayFromHomeBillingRate') ?? 0,
                clientId: watch('clientId'),
                branchId: watch('branch')?.id,
                isDefault: watch('isDefault'),
                effectiveFrom: watch('effectiveFrom'),
              },
              rateType: 'billingRate',
            });
          }
        }}
        openConfirmationDialog={duplicateDialogConfirmOpen}
        setOpenConfirmationDialog={setDuplicateDialogConfirmOpen}
      />
      <DialogTitle sx={{ m: 1 }}>
        {isEdit ? 'Edit Labour Billing Rate' : 'Add Labour Billing Rate'}
      </DialogTitle>
      <Divider />
      <DialogContent>
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Controller
                  name="role"
                  control={control}
                  rules={{ required: 'Role is required' }}
                  render={(field) => (
                    <TextField
                      {...field}
                      select
                      label="Role"
                      fullWidth
                      required
                      error={!!errors.role}
                      helperText={errors.role?.message}>
                      <MenuItem value={Role.Administrator}>
                        Administrator
                      </MenuItem>
                      <MenuItem value={Role.ProjectManager}>
                        Project Manager
                      </MenuItem>
                      <MenuItem value={Role.Supervisor}>Supervisor</MenuItem>
                      <MenuItem value={Role.Technician}>Technician</MenuItem>
                    </TextField>
                  )}
                />
              </Grid>
              <Grid item xs={12}>
                <Controller
                  name="billingRate"
                  control={control}
                  rules={{
                    required: 'Billing rate is required',
                    min: {
                      value: 0,
                      message: 'Billing rate must be 0 or greater',
                    },
                  }}
                  render={(field) => (
                    <TextField
                      {...field}
                      label="Billing Rate"
                      type="number"
                      fullWidth
                      required
                      error={!!errors.billingRate}
                      helperText={errors.billingRate?.message}
                      inputProps={{ step: '0.01', min: '0' }}
                      onChange={(e) =>
                        field.onChange(parseFloat(e.target.value) || 0)
                      }
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">$</InputAdornment>
                        ),
                      }}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12}>
                <Controller
                  name="workingAwayFromHomeBillingRate"
                  control={control}
                  rules={{
                    required: 'Working away from home billing rate is required',
                    min: {
                      value: 0,
                      message:
                        'Working away from home billing rate must be 0 or greater',
                    },
                  }}
                  render={(field) => (
                    <TextField
                      {...field}
                      label="Working Away From Home Billing Rate"
                      type="number"
                      fullWidth
                      required
                      error={!!errors.workingAwayFromHomeBillingRate}
                      helperText={errors.workingAwayFromHomeBillingRate?.message}
                      inputProps={{ step: '0.01', min: '0' }}
                      onChange={(e) =>
                        field.onChange(parseFloat(e.target.value) || 0)
                      }
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">$</InputAdornment>
                        ),
                      }}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12}>
                <Box
                  sx={{
                    backgroundColor: '#f5f5f5',
                    padding: '1rem',
                    borderRadius: '5px',
                  }}>
                  <Controller
                    name="isDefault"
                    key={'isDefault'}
                    control={control}
                    render={(props) => (
                      <FormControlLabel
                        label={`Use as Default Billing Rate`}
                        control={
                          <Checkbox
                            checked={props.value ?? false}
                            onChange={(e) => {
                              if (e.target.checked) {
                                props.onChange('branchId', null);
                                props.onChange('clientId', null);
                              }
                              props.onChange(Boolean(e.target.checked));
                            }}
                            name="removeHeader"
                            color="primary"
                          />
                        }
                      />
                    )}
                  />
                </Box>
              </Grid>
              {!isDefault && (
                <Grid item xs={12}>
                  <BranchAutocomplete
                    name="branch"
                    label="Select Branch"
                    control={control}
                    rules={{
                      required:
                        !isDefault && !clientId
                          ? {
                              value: true,
                              message:
                                'Branch selection is required if Client is not selected',
                            }
                          : false,
                    }}
                    errors={errors}
                    branches={branches}
                    branchId={branch?.id}
                  />
                </Grid>
              )}
              {!isDefault && (
                <Grid item xs={12}>
                  <Controller
                    name="clientId"
                    key={'clientId'}
                    rules={{
                      required:
                        !isDefault && !branch
                          ? {
                              value: true,
                              message:
                                'Client selection is required if Branch is not selected',
                            }
                          : false,
                    }}
                    control={control}
                    render={(field) => (
                      <Autocomplete
                        options={clients.data || []}
                        getOptionLabel={(option) => option.legalName || ''}
                        value={
                          clients.data?.find((c) => c.id === field.value) ??
                          null
                        }
                        onChange={(_, value) =>
                          field.onChange(value?.id ?? undefined)
                        }
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label="Select Client"
                            fullWidth
                          />
                        )}
                      />
                    )}
                  />
                </Grid>
              )}
            </Grid>
          </Grid>
          <Grid item xs={12} md={6}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Typography
                  variant="body1"
                  sx={{ mb: '0.5rem', pl: 0.5, color: colors.grey[600] }}>
                  Effective From
                </Typography>
                <MuiPickersUtilsProvider utils={DateFnsUtils}>
                  <Controller
                    control={control}
                    name={'effectiveFrom'}
                    error={errors.effectiveFrom}
                    rules={{
                      required: {
                        value: true,
                        message: 'Effective From date is required',
                      },
                    }}
                    render={(props) => {
                      return (
                        <DatePicker
                          autoOk
                          fullWidth
                          // className={classes.mTop}
                          orientation="landscape"
                          error={Boolean(errors.effectiveFrom)}
                          variant="static"
                          helperText={errors?.effectiveFrom?.message}
                          openTo="date"
                          value={effectiveFrom}
                          defaultValue={
                            effectiveFrom ? effectiveFrom : new Date()
                          }
                          onChange={(e) => setValue('effectiveFrom', e)}
                        />
                      );
                    }}
                  />
                </MuiPickersUtilsProvider>
                {errors.effectiveFrom && (
                  <Typography
                    variant="caption"
                    color="error"
                    sx={{ paddingTop: 1, display: 'block' }}>
                    {errors.effectiveFrom.message}
                  </Typography>
                )}
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </DialogContent>
      <Divider />
      <DialogActions sx={{ mx: 2, my: 1 }}>
        <Button
          onClick={onClose}
          sx={{
            color: grey[700],
            mr: 1,
          }}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit(onSubmit)}
          variant="contained"
          disabled={isSubmitting}>
          {isSubmitting ? 'Saving...' : 'Save Rate'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
